import type { UnknownAction } from 'redux';
import { put, takeLatest, call, take, select } from 'redux-saga/effects';
import type { AppState } from '~/reducers';
import callRequestSaga from '~/sagas/utils/request-saga';
import {
  loginDIAM as loginDIAMAction,
  loginUser as loginUserAction,
  refreshDIAM as refreshDIAMAction,
  refreshUser as refreshUserAction,
  logoutUser as logoutUserAction,
  clearUser as clearUserAction,
  setLogoutStatus,
} from '~/ducks/authentication/actions';
import DiamAPI from '~/api/diam-api';
import BaseException from '~/utils/fetch-wrapper/errors/BaseException';
import { fetchCurrentUserThunk } from '~/ducks/authentication/thunks';
import { bffWebApi } from '~/api';
import { clearFleetServiceWorkerCaches } from '~/utils/service-worker-utils/service-worker-utils';
import { clearInitialData } from '~/app/initial-data';
import { resetRemoteConfig } from '~/config/firebase/remote-config';
import { LogoutStatus } from '~/domain/auth/models/LogoutStatus';

export function* loginDiam({ username, password }) {
  const response = yield* callRequestSaga(
    DiamAPI.getToken,
    {
      ...loginDIAMAction.actions,
      payload: { username, password },
    },
    username,
    password
  );

  if (!(response instanceof BaseException)) {
    return response;
  }
}

export function* refreshDiam({ token }) {
  const response = yield* callRequestSaga(
    DiamAPI.refreshToken,
    { ...refreshDIAMAction.actions, payload: { token } },
    token
  );

  if (!(response instanceof BaseException)) {
    return response;
  }
}

export function* fetchUserInfoFromFleet() {
  yield put(fetchCurrentUserThunk() as unknown as UnknownAction);
  yield take([
    'authentication/fetchCurrentUser/fulfilled',
    'authentication/fetchCurrentUser/rejected',
  ]);
}

export function* refreshFlow({ payload: { token } }: ReturnType<typeof refreshUserAction>) {
  const userInfo = yield* refreshDiam({ token });

  // Unsuccessful refresh, logout
  if (!userInfo) {
    yield put(clearUserAction());
  }
}

export function* loginFlow({
  payload: { username, password },
}: ReturnType<typeof loginUserAction>) {
  const userInfo = yield* loginDiam({ username, password });

  // Successful login
  if (userInfo) {
    yield* fetchUserInfoFromFleet();
  }
}

export const authSelector = (s: AppState) => s.authentication;

export const logoutStatusSelector = (s: AppState) => s.authentication.logoutStatus;

export function* logoutFlow() {
  const { logoutStatus } = yield select(authSelector);
  if (logoutStatus !== LogoutStatus.Idle) {
    return;
  }
  yield put(setLogoutStatus(LogoutStatus.InProgress));
  yield put(clearUserAction());
  yield call(clearUserData);
  yield put(bffWebApi.util.resetApiState());
  yield put(setLogoutStatus(LogoutStatus.Done));
}

export const clearUserData = async () => {
  clearInitialData();
  await clearFleetServiceWorkerCaches();
  resetRemoteConfig();
};

// Exported for testing
export const redirectToThirdParty = (url: string) => {
  globalThis.location.replace(url);
};

export default [
  function* loginListener() {
    yield takeLatest(loginUserAction.type, loginFlow);
  },

  function* refreshListener() {
    yield takeLatest(refreshUserAction.type, refreshFlow);
  },

  function* logoutListener() {
    yield takeLatest(logoutUserAction.type, logoutFlow);
  },
];
